import { UnknownAction } from "@reduxjs/toolkit"
import { Socket, io } from "socket.io-client"
import { getSocketsUrl } from "@/helpers/getSocketsUrl"
import { ISocketEventPayloads } from "@/store/apis/socketRegistry"

export interface ISocketInitOptions {
  auth?: () => Record<string, string>
  onConnect?: () => void
  onConnectError?: (error: Error) => void
  onDisconnect?: (reason: string) => void
  onReconnect?: (attemptNumber: number) => void
  onReconnectAttempt?: () => void
  onAny?: (event: string, payload: unknown) => void
}

interface ISocketBinding {
  event: string
  handler: (payload: unknown) => void
}

export function createSocketBase(socketName: string) {
  return {
    instance: null as Socket | null,
    dispatch: null as ((action: UnknownAction) => void) | null,
    _subs: 0,
    _bindings: [] as ISocketBinding[],
    _detachManager: null as (() => void) | null,

    _init(options?: ISocketInitOptions) {
      if (this.instance) {
        return
      }
      const auth = options?.auth
      const instance = io(`${getSocketsUrl()}/${socketName}`, {
        auth: auth ? (cb: (data: object) => void) => cb(auth()) : undefined,
        transports: ["websocket"]
      })
      this.instance = instance
      instance.on("connect", () => {
        console.warn(`[${socketName}] socket connected`)
        options?.onConnect?.()
      })
      instance.on("connect_error", (error) => {
        console.warn(`[${socketName}] socket error`)
        options?.onConnectError?.(error)
      })
      instance.on("disconnect", (reason) => {
        console.warn(`[${socketName}] socket disconnect`)
        options?.onDisconnect?.(reason)
      })
      const onReconnect = (attemptNumber: number): void => {
        if (!instance.active) {
          return
        }
        console.warn(`[${socketName}] socket reconnect`)
        options?.onReconnect?.(attemptNumber)
      }
      const onReconnectAttempt = (): void => {
        if (!instance.active) {
          return
        }
        options?.onReconnectAttempt?.()
      }
      instance.io.on("reconnect", onReconnect)
      instance.io.on("reconnect_attempt", onReconnectAttempt)
      this._detachManager = () => {
        instance.io.off("reconnect", onReconnect)
        instance.io.off("reconnect_attempt", onReconnectAttempt)
      }
      if (options?.onAny) {
        instance.onAny(options.onAny)
      }
      for (const binding of this._bindings) {
        instance.on(binding.event, binding.handler)
      }
    },

    setDispatch(dispatch: (action: UnknownAction) => void) {
      this.dispatch = dispatch
    },

    addSub() {
      this._subs += 1
    },

    _reset() {
      const detachManager = this._detachManager
      this._detachManager = null
      try {
        this.instance?.disconnect()
      } finally {
        this.instance = null
        detachManager?.()
      }
    },

    removeSub() {
      this._subs = Math.max(0, this._subs - 1)
      if (this._subs === 0) {
        this._reset()
      }
    },

    _bind<K extends keyof ISocketEventPayloads>(
      event: K,
      handler: (payload: ISocketEventPayloads[K]) => void
    ): () => void {
      const binding: ISocketBinding = {
        event,
        handler: handler as ISocketBinding["handler"]
      }
      this._bindings.push(binding)
      this.instance?.on(binding.event, binding.handler)
      return () => {
        const index = this._bindings.indexOf(binding)
        if (index === -1) {
          return
        }
        this._bindings.splice(index, 1)
        this.instance?.off(binding.event, binding.handler)
      }
    }
  }
}
